import React, { useEffect, useState, useCallback } from 'react';
import HelpIcon from '../Documentation/HelpIcon';
import ConflictIndicator from '../ConflictIndicator';
import ChangeLogModal from '../ChangeLogModal';
import PublishDraftModal from '../PublishDraftModal';
import ShareDraftModal from '../ShareDraftModal.jsx';

// Helper to detect placeholder entries that should not count towards net changes
const isPlaceholderEntry = (entry: any): boolean => {
    if (!entry) return false;
    const data = typeof entry.toObject === 'function' ? entry.toObject() : entry;
    const name = (data?.displayName || data?.course || data?.title || '').toString().trim();
    if (name === '__PLACEHOLDER__') return true;
    if (data?.type === 'placeholder') return true;
    if (data?.additionalProps?.isPlaceholder === true) return true;
    return false;
};

interface FloatingActionButtonsProps {
    // Button visibility controls
    showSaveDraft: boolean;
    showPublishDraft: boolean;
    showConflictIndicator: boolean;
    showReviewChanges: boolean;

    // Button states and data
    isLoading: boolean;
    changeLogLength: number;
    changeLog: any[];
    currentDraftName: string | null;
    isLiveMode: boolean;
    currentConflicts: Array<{
        itemId: string;
        itemName: string;
        day: string;
        time: string;
        room: string;
        reasons: Array<{
            reason: string;
            severity: string;
        }>;
    }>;
    // Add props for change tracking
    scheduledInstances: any[];
    serverBaseline: any[];

    // New: change tracking via ID sets
    changeIdCounts?: { created: number; modified: number; deleted: number };
    hasPendingChangesByIdSets?: boolean;

    // Undo/Redo functionality
    canUndo?: boolean;
    canRedo?: boolean;
    onUndo?: () => boolean;
    onRedo?: () => boolean;

    // Event handlers
    onSaveCurrent: () => void;
    onPublishDraft: (weekRange: { startWeek: number; endWeek: number } | null) => void;
    onToggleConflictList: () => void;
    onSaveChangeLog: () => Promise<any> | any;

    // Data for PublishDraftModal
    timePeriods?: any[];
    selectedYearForView?: number;
    institutionUrlEndpoint?: string;
}

const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({
    // Button visibility controls
    showSaveDraft,
    showPublishDraft,
    showConflictIndicator,
    showReviewChanges,

    // Button states and data
    isLoading,
    changeLog,
    currentDraftName,
    isLiveMode,
    currentConflicts,
    scheduledInstances,
    serverBaseline,
    changeIdCounts,
    hasPendingChangesByIdSets,

    // Undo/Redo functionality
    canUndo = false,
    canRedo = false,
    onUndo,
    onRedo,

    // Event handlers
    onSaveCurrent,
    onPublishDraft,
    onToggleConflictList,
    onSaveChangeLog,

    // Data for PublishDraftModal
    timePeriods = [],
    selectedYearForView = new Date().getFullYear(),
    institutionUrlEndpoint
}) => {
    // State for bounce animation
    const [shouldBounce, setShouldBounce] = useState(false);
    const changeIdCountTotal = (changeIdCounts?.created || 0) + (changeIdCounts?.modified || 0) + (changeIdCounts?.deleted || 0);
    const [previousIdCountTotal, setPreviousIdCountTotal] = useState(changeIdCountTotal);
    const [hasNetChanges, setHasNetChanges] = useState(false);
    const [isChangeLogModalOpen, setIsChangeLogModalOpen] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Function to calculate if there are net changes (similar to useChangeTracking)
    const calculateHasNetChanges = useCallback(() => {
        const baselineReady = Array.isArray(serverBaseline) && serverBaseline.length > 0;
        const instancesReady = Array.isArray(scheduledInstances);
        if (!baselineReady || !instancesReady) {
            console.debug('[FloatingActionButtons] NetChanges early-exit: baseline or instances not ready', {
                baselineReady,
                instancesReady,
                baselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a',
                scheduledLen: Array.isArray(scheduledInstances) ? scheduledInstances.length : 'n/a',
                serverBaselineType: typeof serverBaseline,
                scheduledInstancesType: typeof scheduledInstances,
                serverBaselineSample: Array.isArray(serverBaseline) && serverBaseline.length > 0 ? {
                    id: serverBaseline[0].id,
                    course: serverBaseline[0].course
                } : 'no baseline items'
            });
            return false;
        }

        // Ignore placeholder entries when comparing baseline vs current
        const filteredBaseline = (serverBaseline || []).filter(entry => !isPlaceholderEntry(entry));
        const filteredCurrent = (scheduledInstances || []).filter(entry => !isPlaceholderEntry(entry));

        const baselineItemsMap = new Map(filteredBaseline.map(entry => [entry.id, entry]));
        const currentItemsMap = new Map(filteredCurrent.map(entry => [entry.id, entry]));

        // Check for deleted items (in baseline but not in current)
        for (const baselineEntry of filteredBaseline) {
            if (!currentItemsMap.has(baselineEntry.id)) {
                console.debug('[FloatingActionButtons] NetChanges detected: deleted item', {
                    id: baselineEntry.id,
                    name: baselineEntry.displayName || baselineEntry.course || baselineEntry.title
                });
                return true; // Item was deleted
            }
        }

        // Check for created items (in current but not in baseline)
        for (const currentEntry of filteredCurrent) {
            if (!baselineItemsMap.has(currentEntry.id)) {
                console.debug('[FloatingActionButtons] NetChanges detected: created item', {
                    id: currentEntry.id,
                    name: currentEntry.displayName || currentEntry.course || currentEntry.title
                });
                return true; // Item was created
            }
        }

        // Check for modified items
        for (const baselineEntry of filteredBaseline) {
            const currentEntry = currentItemsMap.get(baselineEntry.id);
            if (currentEntry) {
                // Convert current entry to plain object for comparison
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function'
                    ? currentEntry.toObject()
                    : currentEntry;

                // Compare key fields
                const fieldsToCompare = [
                    'day', 'timeStart', 'timeEnd', 'course', 'teacher', 'room', 'type', 'weekSeries',
                    // Include description so description-only edits are detected in fallback mode
                    'description'
                ];

                for (const field of fieldsToCompare) {
                    const baselineValue = baselineEntry[field];
                    const currentValue = currentEntryData[field];

                    // Handle special cases for objects
                    if (field === 'teacher' || field === 'room') {
                        const normalizeValue = (val: any) => {
                            if (!val) return 'N/A';
                            if (typeof val === 'string') return val;
                            if (typeof val === 'object') return val.name || val.room_code || 'N/A';
                            return String(val);
                        };
                        if (normalizeValue(baselineValue) !== normalizeValue(currentValue)) {
                            console.debug('[FloatingActionButtons] NetChanges detected: modified field', {
                                id: baselineEntry.id,
                                field,
                                baseline: normalizeValue(baselineValue),
                                current: normalizeValue(currentValue)
                            });
                            return true; // Field was modified
                        }
                    } else if (field === 'groups') {
                        const normalizeGroups = (val: any) => {
                            if (!Array.isArray(val)) return 'N/A';
                            return val.map((g: any) => {
                                if (typeof g === 'object' && g !== null) return g.name || g.group_code || g.id;
                                return g;
                            }).join(', ');
                        };
                        if (normalizeGroups(baselineValue) !== normalizeGroups(currentValue)) {
                            console.debug('[FloatingActionButtons] NetChanges detected: modified groups', {
                                id: baselineEntry.id,
                                baseline: normalizeGroups(baselineValue),
                                current: normalizeGroups(currentValue)
                            });
                            return true; // Groups were modified
                        }
                    } else if (baselineValue !== currentValue) {
                        console.debug('[FloatingActionButtons] NetChanges detected: modified primitive field', {
                            id: baselineEntry.id,
                            field,
                            baseline: baselineValue,
                            current: currentValue
                        });
                        return true; // Field was modified
                    }
                }

                // Compare exceptions arrays
                const baselineExceptions = Array.isArray(baselineEntry.exceptions) ? baselineEntry.exceptions : [];
                const currentExceptions = Array.isArray(currentEntryData.exceptions) ? currentEntryData.exceptions : [];

                if (baselineExceptions.length !== currentExceptions.length) {
                    console.debug('[FloatingActionButtons] NetChanges detected: exceptions length change', {
                        id: baselineEntry.id,
                        baselineLen: baselineExceptions.length,
                        currentLen: currentExceptions.length
                    });
                    return true; // Exceptions were added or removed
                }

                // Check if any exceptions were modified
                const baselineExceptionsMap = new Map(
                    baselineExceptions.map((ex: any) => [`${ex.week}-${ex.year}`, ex])
                );
                const currentExceptionsMap = new Map(
                    currentExceptions.map((ex: any) => [`${ex.week}-${ex.year}`, ex])
                );

                // Check each baseline exception against current exceptions
                for (let i = 0; i < baselineExceptions.length; i++) {
                    const baselineException = baselineExceptions[i];
                    const key = `${baselineException.week}-${baselineException.year}`;
                    const currentException = currentExceptionsMap.get(key);
                    if (!currentException || JSON.stringify(baselineException) !== JSON.stringify(currentException)) {
                        console.debug('[FloatingActionButtons] NetChanges detected: exception modified', {
                            id: baselineEntry.id,
                            week: baselineException.week,
                            year: baselineException.year
                        });
                        return true; // Exception was modified
                    }
                }
            }
        }

        return false; // No net changes found
    }, [serverBaseline, scheduledInstances]);



    // Track ID-set change count to trigger bounce animation
    useEffect(() => {
        if (changeIdCountTotal !== previousIdCountTotal) {
            setPreviousIdCountTotal(changeIdCountTotal);
            if (changeIdCountTotal > 0) {
                setShouldBounce(true);
                const timer = setTimeout(() => setShouldBounce(false), 600);
                return () => clearTimeout(timer);
            }
        }
        return undefined;
    }, [changeIdCountTotal, previousIdCountTotal]);

    // Calculate net changes when scheduledInstances or serverBaseline change
    useEffect(() => {
        const netChanges = calculateHasNetChanges();
        setHasNetChanges(netChanges);
        console.debug('[FloatingActionButtons] NetChanges recalculated', {
            baselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a',
            scheduledLen: Array.isArray(scheduledInstances) ? scheduledInstances.length : 'n/a',
            hasNetChanges: netChanges
        });
    }, [calculateHasNetChanges]);

    // Determine if save draft button should be disabled
    // Use net changes for draft mode
    const baselineReady = Array.isArray(serverBaseline) && serverBaseline.length > 0;

    // Prefer ID-set based pending changes signal when provided, fallback to legacy net-changes
    const idSetHasChanges = typeof hasPendingChangesByIdSets === 'boolean' ? hasPendingChangesByIdSets : false;
    const effectiveHasChanges = idSetHasChanges || hasNetChanges;

    // Log why the Save Draft button is enabled/disabled on every relevant change
    useEffect(() => {
        console.debug('[FloatingActionButtons] Save Draft button state', {
            showSaveDraft,
            isLoading,
            baselineReady,
            hasNetChanges,
        });
    }, [showSaveDraft, isLoading, baselineReady, hasNetChanges, idSetHasChanges]);
    // Calculate which buttons are visible to determine spacing
    const hasUndoRedo = canUndo || canRedo;
    const visibleButtons = [
        showSaveDraft,
        showPublishDraft,
        showConflictIndicator,
        showReviewChanges,
        hasUndoRedo
    ].filter(Boolean).length;

    // Base spacing between buttons (in pixels)
    const baseSpacing = 72; // 18 * 4 (tailwind spacing)

    // Calculate dynamic spacing based on visible buttons
    const getButtonSpacing = (index: number): number => {
        if (visibleButtons <= 1) return 0;
        return -10; //baseSpacing * (index);
    };

    let buttonIndex = 0;
    const safeChangeLog = Array.isArray(changeLog) ? (changeLog as never[]) : ([] as never[]);

    // Close ChangeLogModal after successful save in live mode
    const handleChangeLogSave = useCallback(async () => {
        try {
            const maybePromise = onSaveChangeLog && onSaveChangeLog();
            await Promise.resolve(maybePromise);
            if (isLiveMode) {
                setIsChangeLogModalOpen(false);
            }
        } catch (e) {
            // Keep modal open on error
        }
    }, [onSaveChangeLog, isLiveMode]);

    return (
        <div className="fixed bottom-4 right-10 z-50 flex items-center space-x-4">
            {/* Undo/Redo Button Group */}
            {hasUndoRedo && (
                <div className="flex items-center space-x-2" style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}>
                    {/* Undo Button */}
                    <div className="relative group">
                        <button
                            onClick={() => {
                                if (canUndo && onUndo) {
                                    const success = onUndo();
                                    if (success) {
                                        // Visual feedback handled by ClassSchedulerPage via success message
                                    }
                                }
                            }}
                            disabled={!canUndo}
                            className={`rounded-full p-3 shadow-lg transition-colors ${canUndo
                                ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                            title={canUndo ? "Undo (Ctrl+Z / Cmd+Z)" : "Nothing to undo"}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Redo Button */}
                    <div className="relative group">
                        <button
                            onClick={() => {
                                if (canRedo && onRedo) {
                                    const success = onRedo();
                                    if (success) {
                                        // Visual feedback handled by ClassSchedulerPage via success message
                                    }
                                }
                            }}
                            disabled={!canRedo}
                            className={`rounded-full p-3 shadow-lg transition-colors ${canRedo
                                ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                            title={canRedo ? "Redo (Ctrl+Y / Cmd+Shift+Z)" : "Nothing to redo"}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 10H11a8 8 0 00-8 8v2m18-10l-6-6m6 6l-6 6"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Conflict Indicator */}

            {/* Save Draft Button */}
            {showSaveDraft && (
                <div
                    className="relative group"
                    style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}
                >
                    <button
                        onClick={onSaveCurrent}
                        disabled={changeIdCountTotal === 0}
                        className={`relative rounded-full p-4 shadow-lg transition-all duration-200 ease-in-out ${shouldBounce ? 'animate-bounce-once' : ''
                            } ${changeIdCountTotal === 0
                                ? 'bg-gray-300 text-gray-500 pointer-events-none'
                                : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-xl'
                            }`}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                            />
                            <polyline
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                points="17,21 17,13 7,13 7,21"
                            />
                            <polyline
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                points="7,3 7,8 15,8"
                            />
                        </svg>
                        {changeIdCountTotal > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                                {changeIdCountTotal}
                            </span>
                        )}
                    </button>
                    {changeIdCountTotal === 0 && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-100 whitespace-nowrap z-50">
                            No changes to save
                        </div>
                    )}
                    <HelpIcon
                        documentationId="save-draft-button"
                        size={14}
                        position="relative"
                        className="bg-transparent text-gray-600 rounded-full p-1 bottom-[-12px] -left-2 z-10"
                    />
                </div>
            )}

            {/* Publish Draft Button */}
            {showPublishDraft && (
                <div className="relative" style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}>
                    <button
                        onClick={() => setIsPublishModalOpen(true)}
                        disabled={isLoading}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full p-4 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={`Publish "${currentDraftName}" to Live (stays in draft mode)`}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                        </svg>
                    </button>
                    <HelpIcon
                        documentationId="publish-to-live-button"
                        size={14}
                        position="relative"
                        className="bg-transparent text-gray-600 rounded-full p-1 bottom-[-12px] -left-2 z-10"
                    />
                </div>
            )}

            {/* Share Draft Button (shown when in draft mode and have a draft name) */}
            {showPublishDraft && currentDraftName && (
                <div className="relative" style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}>
                    <button
                        onClick={() => setIsShareModalOpen(true)}
                        disabled={isLoading}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={`Share draft "${currentDraftName}" via public link`}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 11-5.656-5.656l1.172-1.172M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 115.656 5.656L17.656 10" />
                        </svg>
                    </button>
                    <HelpIcon
                        documentationId="share-draft-button"
                        size={14}
                        position="relative"
                        className="bg-transparent text-gray-600 rounded-full p-1 bottom-[-12px] -left-2 z-10"
                    />
                </div>
            )}


            {/* Review Changes Button */}
            {showReviewChanges && (
                <div className="relative" style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}>
                    <button
                        onClick={() => setIsChangeLogModalOpen(true)}
                        disabled={changeIdCountTotal === 0}
                        className={`rounded-full p-4 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center w-14 h-14 transition-all duration-200 ease-in-out ${changeIdCountTotal === 0
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed ring-gray-300'
                            : 'bg-white text-gray-700 hover:bg-gray-50 ring-gray-400'
                            }`}
                        title={`Review ${changeIdCountTotal} Changes`}
                        aria-label={`Review ${changeIdCountTotal} Changes`}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        {changeIdCountTotal > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                                {changeIdCountTotal}
                            </span>
                        )}
                    </button>
                    <HelpIcon
                        documentationId="review-changes-button"
                        size={14}
                        position="relative"
                        className="bg-transparent text-gray-600 rounded-full p-1 bottom-[-12px] -left-2 z-10"
                    />
                </div>
            )}
            {showConflictIndicator && (
                <div className="relative" style={{ marginRight: `${getButtonSpacing(buttonIndex++)}px` }}>
                    <ConflictIndicator
                        conflictCount={currentConflicts.length}
                        onClick={onToggleConflictList}
                    />
                </div>
            )}

            {/* Change Log Modal moved here */}
            <ChangeLogModal
                isOpen={isChangeLogModalOpen}
                onClose={() => setIsChangeLogModalOpen(false)}
                changeLog={safeChangeLog}
                onSave={handleChangeLogSave}
                isSaving={isLoading}
                saveButtonText={isLiveMode ? 'Save to Live' : `Save to Draft "${currentDraftName || ''}"`}
            />

            {/* Publish Draft Modal */}
            <PublishDraftModal
                isOpen={isPublishModalOpen}
                onClose={() => setIsPublishModalOpen(false)}
                onPublish={onPublishDraft}
                currentDraftName={currentDraftName || ''}
                timePeriods={timePeriods}
                scheduledInstances={scheduledInstances}
                selectedYearForView={selectedYearForView}
            />

            {/* Share Draft Modal */}
            <ShareDraftModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                currentDraftName={currentDraftName || ''}
                institutionUrlEndpoint={institutionUrlEndpoint || ''}
            />
        </div>
    );
};

export default FloatingActionButtons;
